import { CdkDrag, CdkDragPlaceholder, CdkDragPreview, CdkDropList } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DraggedRowPreviewComponent } from '@hub4b/controls/datas-list/dragged-row-preview/dragged-row-preview.component';
import { ExpandedCommentCellComponent } from '@hub4b/controls/datas-list/expanded-comment-cell/expanded-comment-cell.component';
import { ExpandedDetailCellComponent } from '@hub4b/controls/datas-list/expanded-detail-cell/expanded-detail-cell.component';
import { TableActionCellComponent } from '@hub4b/controls/datas-list/table-action-cell/table-action-cell.component';
import { TableCellComponent } from '@hub4b/controls/datas-list/table-cell/table-cell.component';
import { TableHeaderCellComponent } from '@hub4b/controls/datas-list/table-header-cell/table-header-cell.component';
import { TableWorkflowCellComponent } from '@hub4b/controls/datas-list/table-workflow-cell/table-workflow-cell.component';
import { ColumnResizeDirective } from '@hub4b/directives/column-resize.directive';
import { FormFocusDirective } from 'src/app/core/directives/form-focus.directive';
import { DateHelper } from 'src/app/helpers/date-helper';
import { DownloadHelper } from 'src/app/helpers/download-helper';
import { FolderCounterUpdatedEvent } from 'src/app/models/events/folder-counter-updated-event';
import { DialogService } from 'src/app/services/actions/dialog.service';
import { FolderActionsService } from 'src/app/services/actions/folder-actions.service';
import { NotificationService } from 'src/app/services/actions/notification.service';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { EventsManagerService } from 'src/app/services/events/events-manager.service';
import { FolderCounterEventsService } from 'src/app/services/events/folder-counter-events.service';
import { FolderListEventsService } from 'src/app/services/events/folder-list-events.service';
import { FolderCommentsService } from 'src/app/services/http/folder-comments.service';
import { FolderDocumentsService } from 'src/app/services/http/folder-documents.service';
import { FoldersService } from 'src/app/services/http/folders.service';
import { UsersService } from 'src/app/services/http/users.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { FolderCommentsStateService } from 'src/app/services/state/folder-comments-state.service';
import { NavigationStateService } from 'src/app/services/state/navigate-state.service';
import { ProfileSettingsStateService } from 'src/app/services/state/profile-settings-state.service';
import { ViewsStateService } from 'src/app/services/state/views-state.service';
import { BaseDatasListComponent } from '../base-datas-list/base-datas-list.component';
import { BaseDatasource } from '../base-datas-list/data-sources/base-datasource';
import { WorkflowDatasource } from './data-sources/workflow-datasource';

@Component({
    selector: 'app-workflow-datas-list',
    templateUrl: './../base-datas-list/base-datas-list.component.html',
    styleUrls: ['./../base-datas-list/base-datas-list.component.scss', './workflow-datas-list.component.scss'],
    providers: [
        {
            provide: BaseDatasource,
            useClass: WorkflowDatasource,
            deps: [FoldersService, ViewsStateService],
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        FormFocusDirective,
        MatTableModule,
        MatSortModule,
        CdkDropList,
        NgClass,
        TableHeaderCellComponent,
        TableCellComponent,
        TableWorkflowCellComponent,
        TableActionCellComponent,
        ExpandedDetailCellComponent,
        ExpandedCommentCellComponent,
        CdkDrag,
        CdkDragPlaceholder,
        CdkDragPreview,
        DraggedRowPreviewComponent,
        ColumnResizeDirective,
    ]
})
export class WorkflowDatasListComponent extends BaseDatasListComponent implements OnInit, OnDestroy {
    /**
     * Constructeur
     * @param usersService : Service de gestion des utilisateurs.
     * @param foldersService : Service de gestion des dossiers.
     * @param boxesStateService : Service de persistence de l'état des bannettes.
     * @param navigateService : Service de gestion de la navigation entre dossiers.
     * @param folderSearchStateService : Service de persistence de l'état du formulaire de recherche.
     * @param folderActionsService : Service des actions relatives aux dossiers.
     * @param dialogService : Service de boite de dialogue.
     * @param notificationService : Service de notification.
     * @param cdRef : Detecteur de changement.
     * @param foldersTableRef : Référence du tableau de dossiers dans le DOM.
     */
    constructor(
        protected override connectedUserStateService: ConnectedUsersStateService,
        protected override usersService: UsersService,
        protected override foldersService: FoldersService,
        protected override navigateService: NavigationStateService,
        protected override folderActionsService: FolderActionsService,
        protected override folderCommentStateService: FolderCommentsStateService,
        protected override dialogService: DialogService,
        protected override viewsStateService: ViewsStateService,
        protected override notificationService: NotificationService,
        foldersCommentsService: FolderCommentsService,
        protected override cdRef: ChangeDetectorRef,
        protected override foldersTableRef: ElementRef,
        protected override datasource: BaseDatasource,
        protected override spinner: SpinnerService,
        protected override folderListEventsService: FolderListEventsService,
        protected override eventsManagerService: EventsManagerService,
        protected override folderDocumentsService: FolderDocumentsService,
        protected override folderCounterEventsService: FolderCounterEventsService,
        protected override downloadHelper: DownloadHelper,
        protected override profileSettingsStateService: ProfileSettingsStateService,
        protected override dateHelper: DateHelper,
        @Inject(LOCALE_ID) protected override locale: string
    ) {
        super(
            connectedUserStateService,
            usersService,
            foldersService,
            navigateService,
            folderActionsService,
            folderCommentStateService,
            dialogService,
            notificationService,
            foldersCommentsService,
            cdRef,
            foldersTableRef,
            datasource,
            spinner,
            viewsStateService,
            folderListEventsService,
            eventsManagerService,
            folderDocumentsService,
            folderCounterEventsService,
            downloadHelper,
            profileSettingsStateService,
            dateHelper,
            locale
        );
    }

    /**
     * Appelée à l'initialisation du composant.
     */
    override ngOnInit(): void {
        super.ngOnInit();
        this.isWorkflowStepDateVisible = this.connectedUserStateService.getUserConnected().params.isWorkflowStepDateVisible === true;
        this.expandComments = this.connectedUserStateService.getUserConnected().params.expandComments === true;
    }

    /**
     * Appelée à la destruction du composant.
     */
    override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    /**
     * Appelée une fois la configuration chargée.
     * @param viewId: Id de la vue.
     */
    protected override afterConfigurationLoaded(viewId: number) {
        super.afterConfigurationLoaded(viewId);
        this.tableDataSource.data = [];
    }

    /**
     * Appelée une fois les dossiers chargés.
     */
    protected override afterFoldersLoaded() {
        this.folderCounterEventsService.displayedFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.loadedFoldersCount));
    }

    /**
     * Restaure la position de la barre de défilement (Utilisé lorsque l'on revient sur l'onglet de la liste après en avoir selectionné un autre). Sinon la barre de défilement est remise en haut.
     */
    public restoreScrollPosition() {
        setTimeout(() => {
            if (this.resultsContainerElementRef) {
                var nativ = this.resultsContainerElementRef.nativeElement;
                nativ.scrollTop = this.currentScrollTop;
                nativ.scrollLeft = this.currentScrollLeft;
            }
        }, 500);
    }
}
